import { Dispatch, useEffect, useMemo } from 'react';
import { useImmerReducer, Reducer } from 'use-immer';
import * as _ from 'lodash';
import {
  EnrichedSettingsTreeDto,
  normalizeSettingsNodeStyle,
  SettingsState,
  SettingsTreeDataRequestType
} from './settings-tree-reducer.types';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { SettingsTreeAction } from './settings-tree-action-types';
import { settingsTreeActionHandlers } from './settings-tree-action-handlers';
import { generateInitialFormState } from './settings-tree-reducer-helpers';
import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQueriesGetSettingsTreeProjectChildren } from '../settings.queries';
import { useHasUIFeatures } from '../../../core/permissions/has-permissions.hook';

export type SettingsActionDispatcher = Dispatch<SettingsTreeAction>;

const reducer: Reducer<SettingsState, SettingsTreeAction> = (state, action) => {
  // Log the action for debugging purposes
  console.debug('SettingsTreeReducer action:', action);
  switch (action.type) {
    case 'SELECT_NODE':
      return settingsTreeActionHandlers.selectNode(state, action);
    case 'ENQUEUE_NODE_SELECTION':
      return settingsTreeActionHandlers.enqueueNodeSelection(state, action);
    case 'CLEAR_ENQUEUED_NODE_SELECTION':
      return settingsTreeActionHandlers.clearEnqueuedNodeSelection(state, action);
    case 'TOGGLE_NODE':
      return settingsTreeActionHandlers.toggleNode(state, action);
    case 'CREATE_NODE':
      return settingsTreeActionHandlers.createNode(state, action);
    case 'REGISTER_FORM':
      return settingsTreeActionHandlers.registerForm(state, action);
    case 'UPDATE_FORM':
      return settingsTreeActionHandlers.updateForm(state, action);
    case 'RESET_FORM':
      return settingsTreeActionHandlers.resetForm(state, action);
    case 'ACKNOWLEDGE_FORM_SUCCESS':
      return settingsTreeActionHandlers.acknowledgeFormSuccess(state, action);
    case 'SUBMIT_FORM':
      return settingsTreeActionHandlers.submitForm(state, action);
    case 'SUBMIT_FORM_SUCCESS':
      return settingsTreeActionHandlers.submitFormSuccess(state, action);
    case 'SUBMIT_FORM_ERROR':
      return settingsTreeActionHandlers.submitFormError(state, action);
    case 'UPDATE_SETTINGS_TREE':
      return settingsTreeActionHandlers.updateSettingsTree(state, action);
    case 'UPDATE_SETTINGS_TREE_CHILDREN':
      return settingsTreeActionHandlers.updateSettingsTreeChildren(state, action);
    case 'SET_CURRENT_ACTION':
      return settingsTreeActionHandlers.setCurrentAction(state, action);
    case 'CLEAR_CURRENT_ACTION':
      return settingsTreeActionHandlers.clearCurrentAction(state, action);
    default:
      break;
  }
};

function makeInitialState(settingsTreeDto: EnrichedSettingsTreeDto, readOnly: boolean = false): SettingsState {
  const root = settingsTreeDto.nodes.find((n) => n.type === KnownSettingsNodeDtoType.Root);
  const rootProject = settingsTreeDto.nodes.find((n) => n.parentId === root?.id);
  const expandedNodeIds: string[] = [];
  if (rootProject) expandedNodeIds.push(rootProject.id);
  return {
    projectTree: _.cloneDeep(settingsTreeDto),
    projectChildrenTree: { nodes: [] },
    placeholderNodes: [],
    selectedNode: rootProject,
    readOnly,
    forms: {
      [rootProject?.id || '']: generateInitialFormState(
        rootProject?.id || '',
        rootProject?.type as KnownSettingsNodeDtoType,
        SettingsTreeDataRequestType.Projects,
        'edit',
        readOnly
      )
    },
    expandedNodeIds
  };
}

/**
 * Custom hook to manage the state of the settings tree and editor panel.
 * @param settingsTreeDto The settings tree data returned by the API.
 * @returns The state and dispatcher for the settings tree.
 * @example
 * const { state, dispatch } = useSettingsTreeReducer(settingsTreeDto);
 */
export function useSettingsTreeReducer(settingsTreeDto: EnrichedSettingsTreeDto) {
  const hasRequiredFeature = useHasUIFeatures(['internal-groundfloor']);
  // If the feature is not available, we set the readOnly flag to true
  const readOnly = !hasRequiredFeature.hasAllFeatures;

  const [state, dispatch] = useImmerReducer(reducer, makeInitialState(settingsTreeDto, readOnly));
  const queryClient = useQueryClient();

  const currentFormState = useMemo(
    () => state.forms[state.selectedNode?.id || ''] || null,
    [state.forms, state.selectedNode?.id]
  );

  useEffect(() => {
    // We need to reload the tree when a form is successfully submitted.
    // Even if a node is being edited, the tree might have changed.
    // For example, if a Project's type is changed, that will change the Icon
    // for that Project in the tree.
    if (currentFormState?.isSuccess) {
      queryClient.invalidateQueries(QueryKeys.SETTINGS);
    }
  }, [currentFormState?.isSuccess, queryClient]);

  useEffect(() => {
    dispatch({ type: 'UPDATE_SETTINGS_TREE', payload: settingsTreeDto });
  }, [dispatch, settingsTreeDto]);

  // Watch for enqueued node selections and select the node when it is available
  useEffect(() => {
    const enqueued = state.enqueuedNodeSelection;
    if (!enqueued) {
      return;
    }
    const nodeSource =
      enqueued.requestType === SettingsTreeDataRequestType.Projects ? state.projectTree : state.projectChildrenTree;

    // A node can appear multiple times in the tree, so we disambiguate the occurrence we want by:
    //  - parentNodeId, when the caller knows the expected parent tree-node (post-create flow), or
    //  - rootId, the managing project the occurrence belongs to (deep-link flow).
    // We do this even if a node is already selected, because the parent/root might have changed.
    const node = nodeSource.nodes.find((n) => {
      if (n.entityId !== enqueued.entityId) {
        return false;
      }
      if (enqueued.parentNodeId !== undefined) {
        return n.parentId === enqueued.parentNodeId;
      }
      if (enqueued.rootId !== undefined) {
        return n.request.rootId === enqueued.rootId;
      }
      return true;
    });
    if (node) {
      dispatch({
        type: 'SELECT_NODE',
        payload: {
          nodeId: node.id,
          isPlaceholder: false,
          requestType: enqueued.requestType,
          expandNode: enqueued.expandNode
        }
      });
    }
  }, [
    dispatch,
    state.selectedNode?.id,
    state.enqueuedNodeSelection,
    state.placeholderNodes,
    state.projectTree,
    state.projectChildrenTree
  ]);

  const expandedProjects = useMemo(() => {
    return [
      ...new Set(
        state.expandedNodeIds
          .map((id) => {
            const node = state.projectTree.nodes.find((n) => n.id === id);
            return node?.type === KnownSettingsNodeDtoType.Project ? node.entityId : undefined;
          })
          .filter((id): id is string => !!id)
      )
    ];
  }, [state.expandedNodeIds, state.projectTree.nodes]);

  const projectChildren = useQueriesGetSettingsTreeProjectChildren(expandedProjects);

  // True once every in-flight project-children query has settled (loaded or errored).
  const projectChildrenSettled = projectChildren.every((query) => !query.isLoading);

  // True while any expanded project's children are loading for the first time (no cached data yet),
  // so the tree can show a loading indicator instead of appearing empty/stalled.
  const projectChildrenLoading = projectChildren.some((query) => query.isLoading);

  // The flattened, de-duplicated set of child nodes derived from the expanded projects' queries.
  const projectChildrenNodes = useMemo(() => {
    const allNodes = projectChildren
      .filter((query) => query.data?.nodes)
      .map((query, idx) => query.data!.nodes.map((node) => ({ node, rootId: expandedProjects[idx] })))
      .flat()
      .map(({ node, rootId }) => ({
        ...node,
        style: normalizeSettingsNodeStyle(node.style),
        isPlaceholder: false,
        request: {
          type: SettingsTreeDataRequestType.ProjectChildren,
          rootId
        }
      }));

    // Some child nodes can appear in more than one expanded project's response.
    // Keep only one instance per deterministic node ID to prevent visual duplicates in the tree.
    return Array.from(new Map(allNodes.map((n) => [n.id, n])).values());
  }, [projectChildren, expandedProjects]);

  useEffect(() => {
    // When the project children change, we fully update the tree with all the project children data.
    // This is an agressive appraoch, but it is the simplest way to handle all edge cases.
    // To prevent infinite loops, we only dispatch if there are changes.
    // Deep equality catches both structural changes (added/removed nodes) and
    // property changes (e.g. a hub name update that leaves the node IDs unchanged).
    if (!_.isEqual(projectChildrenNodes, state.projectChildrenTree.nodes)) {
      dispatch({
        type: 'UPDATE_SETTINGS_TREE_CHILDREN',
        payload: {
          nodes: projectChildrenNodes
        }
      });
    }
  }, [dispatch, projectChildrenNodes, state.projectChildrenTree.nodes]);

  // Whether an enqueued *deep-link* selection can no longer be satisfied: the relevant tree has
  // finished loading and is reflected in state, yet the target node is absent. Derived from the
  // merged tree (not just the query flags) so it can't fire in the window between "queries settled"
  // and "children merged + selected" — that would produce a false "not found". The post-create flow
  // (which uses parentNodeId) is excluded; only deep-links waiting on `rootId` are considered.
  const deepLinkUnresolved = useMemo(() => {
    const enqueued = state.enqueuedNodeSelection;
    if (!enqueued || enqueued.parentNodeId !== undefined) {
      return false;
    }
    const usesChildren = enqueued.requestType !== SettingsTreeDataRequestType.Projects;
    // The project tree is provided up-front; only the children tree loads lazily and must be in sync.
    const sourceSettled = usesChildren
      ? projectChildrenSettled && _.isEqual(projectChildrenNodes, state.projectChildrenTree.nodes)
      : true;
    if (!sourceSettled) {
      return false;
    }
    const source = usesChildren ? state.projectChildrenTree : state.projectTree;
    const found = source.nodes.some(
      (n) => n.entityId === enqueued.entityId && (enqueued.rootId === undefined || n.request.rootId === enqueued.rootId)
    );
    return !found;
  }, [
    state.enqueuedNodeSelection,
    state.projectTree,
    state.projectChildrenTree,
    projectChildrenNodes,
    projectChildrenSettled
  ]);

  return {
    state,
    dispatch,
    currentFormState,
    deepLinkUnresolved,
    projectChildrenLoading
  };
}
